import { Suspense } from "react";
import { parseUrl } from "../../../../../lib/link-utils.js";
import PeertubeVideoEmbed from "../../../../diary/ShowroomForm/FormFields/FileUpload/PeertubeVideoEmbed.jsx";
import { ImgForPath } from "../../../../../lib/images.jsx";
import { PreviewImg } from "../PreviewImg/PreviewImg.jsx";
import Icon from "../../../../../design-system/components/Icon/Icon.jsx";
import { MODELS, useMultiModel } from "../../../../../api";
import { Spinner } from "../../../../../design-system/utils/Spinner/Spinner.jsx";
import ErrorBoundary from "../../../../../lib/ErrorBoundary";
import * as styles from "./UploadContent.css.ts";

/**
 * Retrieve a filename from a path.
 *
 * @param path
 * @return {*|null}
 */
const pathToFileName = (path) => {
  if (!path) return null;
  const slices = path.split("/");
  return slices[slices.length - 1];
};

/**
 * FileDownloader is a functional component that facilitates downloading or opening files.
 * It constructs a file URL based on the provided path and renders a styled UI element
 * which acts as a download link for the file.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.path - The relative path to the file that needs to be downloaded or accessed.
 *
 * @returns {JSX.Element} A clickable row component that, when clicked, opens the file in a new tab.
 * If the `path` is invalid or the file URL cannot be resolved, a spinner is displayed instead.
 */
const FileDownloaderInner = ({ path }) => {
  const [modelData] = useMultiModel([path ? MODELS.S3_FILE_URL(path) : null]);
  const src = modelData?.fileUrl;

  if (!src) return <Spinner />;

  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className={styles.linkContent}
    >
      <Icon size="48px" name="two_pager" />
      <div className={styles.linkTexts}>
        <span className={styles.linkLabel}>Öffnen</span>
        <span className={styles.linkSublabel}>{pathToFileName(path)}</span>
      </div>
    </a>
  );
};

/**
 * FileDownloader is isolated in its own Suspense + ErrorBoundary so a missing
 * upload (the `/upload` lookup 404s) degrades to an inline notice instead of
 * throwing up to `RouteBoundary` and blanking the whole page. Keyed by `path` so
 * switching files resets the boundary.
 */
const FileDownloader = ({ path }) => (
  <ErrorBoundary
    key={path}
    fallback={
      <div className={styles.linkContent}>
        <Icon size="48px" name="two_pager" />
        <div className={styles.linkTexts}>
          <span className={styles.linkLabel}>Datei nicht verfügbar</span>
          <span className={styles.linkSublabel}>{pathToFileName(path)}</span>
        </div>
      </div>
    }
  >
    <Suspense fallback={<Spinner />}>
      <FileDownloaderInner path={path} />
    </Suspense>
  </ErrorBoundary>
);

const compByType = {
  video: {
    Comp: ({ data }) => <PeertubeVideoEmbed data={data} />,
    needsCover: () => false,
  },
  link: {
    Comp: ({ data: link }) => {
      const parsed = parseUrl(link);
      return (
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          className={styles.linkContent}
        >
          <Icon size="48px" name="open" />
          {parsed ? (
            <div className={styles.linkTexts}>
              <span className={styles.linkLabel}>Öffnen</span>
              <span className={styles.linkSublabel}>{parsed.hostname}</span>
            </div>
          ) : (
            <span className={styles.linkLabel}>{link}</span>
          )}
        </a>
      );
    },
    needsCover: () => true,
  },
  file: {
    Comp: ({ data: { path, probe } }) =>
      probe && probe.width ? (
        <PreviewImg
          path={path}
          height={400}
          width={800}
          className={styles.coverImg}
        />
      ) : (
        <FileDownloader path={path} />
      ),
    needsCover: ({ data }) => !(data.probe && data.probe.width),
    isCoverFn: ({ data }) => !(data.probe && data.probe.width),
  },
};

export const UploadContent = ({ data, coverFileData }) => {
  if (!data) {
    if (!coverFileData) return null;
    return (
      <ImgForPath
        path={coverFileData.path || coverFileData.data?.path}
        height={400}
        width={800}
        className={styles.coverImg}
        alt="Projekt Vorschaubild"
      />
    );
  } else {
    const { Comp, needsCover, isCoverFn } = compByType[data.type];
    const withCover = needsCover(data);
    const isCover = isCoverFn && isCoverFn({ data });
    if (withCover) {
      return (
        <figure className={styles.container}>
          {coverFileData ? (
            <ImgForPath
              path={coverFileData.path || coverFileData.data?.path}
              height={400}
              width={800}
              className={styles.coverImg}
              alt="Hintergrundbild für Projekt-Inhalt"
            />
          ) : (
            <div className={styles.placeholder} aria-hidden="true" />
          )}
          <figcaption className={styles.overlay}>
            {Comp && (
              <div className={styles.linkBadge}>
                <Comp data={data.data} />
              </div>
            )}
          </figcaption>
        </figure>
      );
    } else if (isCover) {
      return <Comp data={data.data} />;
    } else {
      return (
        <div className={styles.standaloneWrapper}>
          <div className={styles.mediaContainer}>
            {Comp && <Comp data={data.data} />}
          </div>
        </div>
      );
    }
  }
};
